import { useState } from 'react';
import { useIntl } from 'react-intl';
import { IconPalette } from '@rossum/ui/icons/tabler';
import {
  Box,
  Button,
  Menu,
  Stack,
  SvgIcon,
  TextField,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';

const labelColors = [
  '#E11D48',
  '#F97316',
  '#EAB308',
  '#10B981',
  '#22D3EE',
  '#3B82F6',
  '#6366F1',
  '#8B5CF6',
  '#EC4899',
  '#6B7280',
  '#14B8A6',
  '#A855F7',
  '#F43F5E',
  '#84CC16',
];

type ColorPickerProps = {
  onColorChange: (color: string) => void;
  color?: string | null;
};

const isCorrectColor = (color: string) => {
  const s = new Option().style;
  s.color = color;
  return s.color !== '';
};

export const ColorPicker = ({ onColorChange, color }: ColorPickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const intl = useIntl();

  const colorIsValid = isCorrectColor(color || '');
  const colorIsParty = color === 'party';

  return (
    <Stack direction="row" width={1} spacing={1}>
      <TextField
        label={intl.formatMessage({
          id: 'containers.settings.labels.dialogs.fields.labelColor',
        })}
        value={color}
        onChange={e => onColorChange(e.target.value)}
        size="small"
        fullWidth
        slotProps={{
          input: {
            startAdornment: color ? (
              <Box
                sx={{
                  position: 'relative',
                  width: 18,
                  height: 16,
                  borderRadius: borderRadius('s'),
                  backgroundColor: t =>
                    colorIsValid ? color : t.palette.common.white,
                  backgroundImage: colorIsParty
                    ? 'linear-gradient(70deg, #ff0000, #ff8c00, #ffff00, #32cd32, #00ced1, #1e90ff, #8a2be2, #ff1493, #ff69b4, #ffa500, #00ff7f, #7fffd4)'
                    : 'none',
                  mr: 1,
                  '&::after': {
                    content: colorIsValid || colorIsParty ? 'none' : '""',
                    position: 'absolute',
                    display: 'block',
                    right: '7px',
                    top: '-2px',
                    width: '2px',
                    height: '20px',
                    backgroundColor: t => t.palette.error.main,
                    transform: 'rotate(45deg)',
                  },
                }}
              />
            ) : null,
          },
        }}
      />
      <Button
        onClick={e => setAnchorEl(e.currentTarget)}
        variant="outlined"
        size="small"
        color="secondary"
      >
        <SvgIcon fontSize="medium">
          <IconPalette />
        </SvgIcon>
      </Button>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Stack
          p={2}
          direction="row"
          spacing={2}
          useFlexGap
          sx={{ maxWidth: 450, flexWrap: 'wrap', justifyContent: 'center' }}
        >
          {labelColors.map(c => (
            <Box
              key={c}
              onClick={() => {
                onColorChange(c);
                setAnchorEl(null);
              }}
              sx={{
                width: 44,
                height: 44,
                backgroundColor: c,
                borderRadius: borderRadius('s'),
                cursor: 'pointer',
              }}
            />
          ))}
        </Stack>
      </Menu>
    </Stack>
  );
};
